/*
 * OzMobiles: complete the variant list for products with more than 250 variants.
 *
 * Liquid's product.variants stops at 250, so the product JSON in snippets/product-info.liquid holds
 * only the first 250. theme.js resolves every option combination, price, stock and the add to cart
 * id from that list, so on a 2,000 variant watch most colours showed as crossed out and could not be
 * bought. This fetches the remaining pages from templates/product.oz-variants.liquid, merges them
 * into the same JSON, adds the missing ids to the master select, and re-initialises the product
 * section through the theme's own section load events, so all of theme.js and custom.js (condition
 * rules, gallery sync, swatch availability) run unchanged on the full list.
 *
 * Does nothing unless the page JSON holds exactly 250 variants and the options allow more.
 */
(function () {
  'use strict';

  var PAGE_SIZE = 250;

  function readJson(el) {
    try { return JSON.parse(el.textContent || el.innerHTML); } catch (e) { return null; }
  }

  function combinations(data) {
    return (data.options_with_values || []).reduce(function (n, option) {
      return n * ((option.values && option.values.length) || 1);
    }, 1);
  }

  function pageUrl(page) {
    var url = new URL(window.location.href);
    url.searchParams.delete('variant');
    url.searchParams.set('view', 'oz-variants');
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  function fetchPage(page) {
    return fetch(pageUrl(page), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) { throw new Error('variant page ' + page + ': ' + response.status); }
        return response.text();
      })
      .then(function (text) { return JSON.parse(text); });
  }

  // The option values the customer has selected right now, read from the checked inputs.
  function selectedOptions(section, data) {
    var chosen = {};
    (data.options_with_values || []).forEach(function (option, index) {
      var input = section.querySelector('input[data-option-position="' + (index + 1) + '"]:checked');
      if (input) { chosen['option' + (index + 1)] = input.value; }
    });
    return chosen;
  }

  function matches(variant, chosen) {
    return Object.keys(chosen).every(function (key) { return variant[key] === chosen[key]; });
  }

  function addMasterOptions(section, productId, variants) {
    var select = section.querySelector('#product-select-' + productId);
    if (!select) { return; }
    var known = {};
    Array.prototype.forEach.call(select.options, function (option) { known[option.value] = true; });
    variants.forEach(function (variant) {
      if (known[String(variant.id)]) { return; }
      var option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      if (variant.sku) { option.setAttribute('data-sku', variant.sku); }
      if (!variant.available) { option.disabled = true; }
      select.appendChild(option);
    });
  }

  // The add to cart form posts this select's value, so it must point at the selected variant.
  function markMasterSelected(section, productId, variantId) {
    var select = section.querySelector('#product-select-' + productId);
    var option = select && select.querySelector('option[value="' + variantId + '"]');
    if (!option) { return; }
    Array.prototype.forEach.call(select.querySelectorAll('option[selected]'), function (other) {
      other.removeAttribute('selected');
    });
    option.setAttribute('selected', 'selected');
    select.value = String(variantId);
  }

  function reinitialise(section) {
    var sectionId = section.getAttribute('data-section-id');
    var wrapper = document.getElementById('shopify-section-' + sectionId) || section.parentElement;
    document.dispatchEvent(new CustomEvent('shopify:section:unload', { detail: { sectionId: sectionId } }));
    wrapper.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: sectionId } }));
    document.dispatchEvent(new CustomEvent('oz:product-json-updated', { detail: { sectionId: sectionId } }));
  }

  function complete(jsonElement) {
    var data = readJson(jsonElement);
    if (!data || !data.product || !data.product.variants) { return; }
    var variants = data.product.variants;
    if (variants.length !== PAGE_SIZE || combinations(data) <= PAGE_SIZE) { return; }

    var section = jsonElement.closest('[data-section-type="product"]');
    if (!section) { return; }

    // Every page at once: the option combinations bound the page count, and a page past the last
    // one comes back with no variants, so asking for one too many costs nothing but a request.
    var lastPage = Math.ceil(combinations(data) / PAGE_SIZE);
    var requests = [];
    for (var page = 2; page <= lastPage; page++) { requests.push(fetchPage(page)); }

    Promise.all(requests).then(function (pages) {
      var seen = {};
      variants.forEach(function (variant) { seen[variant.id] = true; });
      var added = [];
      data.inventories = data.inventories || {};
      pages.forEach(function (page) {
        page.variants.forEach(function (variant) {
          if (seen[variant.id]) { return; }
          seen[variant.id] = true;
          variants.push(variant);
          added.push(variant);
        });
        Object.keys(page.inventories || {}).forEach(function (id) { data.inventories[id] = page.inventories[id]; });
      });
      if (!added.length) { return; }
      // theme.js only hides option values that do not exist once it knows the list is whole.
      data.product.oz_variants_complete = true;

      // Keep whatever the customer has already picked, if it is now a real variant.
      var chosen = selectedOptions(section, data);
      var current = variants.filter(function (variant) { return matches(variant, chosen); })[0];
      if (current) { data.selected_variant_id = current.id; }

      jsonElement.textContent = JSON.stringify(data);
      addMasterOptions(section, data.product.id, added);
      markMasterSelected(section, data.product.id, data.selected_variant_id);
      // The gallery is rebuilt by the reload and starts on data-initial-media-id; point it at the
      // selected variant's photo, or it falls back to the photo the page was rendered with.
      var gallery = section.querySelector('.product-gallery__carousel[data-initial-media-id]');
      if (gallery && current && current.featured_media) {
        gallery.setAttribute('data-initial-media-id', String(current.featured_media.id));
      }
      reinitialise(section);

      // A choice made before the list arrived had no variant to put in the address bar yet.
      var url = new URL(window.location.href);
      if (current && url.searchParams.get('variant') !== String(current.id)) {
        url.searchParams.set('variant', String(current.id));
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }
    }).catch(function (error) {
      if (window.console) { console.warn('[oz-high-variants]', error); }
    });
  }

  function run() {
    document.querySelectorAll('[data-section-type="product"] [data-product-json]').forEach(complete);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
